import React, { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { useSelector } from "react-redux"
import { Add, Delete } from "@material-ui/icons"
import { useTheme } from "@material-ui/core/styles"
import { generateFields, getOrderedFieldsToRender, isObjectEmpty, useAutoCompleteStyles } from "../../../../../utils"
import config from "../../../../../config"
import { Collapse, Button, FormControl, TextField } from "@material-ui/core"
import CustomFormRowTitle from "./CustomRow/CustomRowTitle"
import { renderField } from "./RenderField"
import { Autocomplete } from "@material-ui/lab"

export default function Section({ formik, section }) {

    const { t, i18n: { language: i18nLanguage } } = useTranslation()
    const { theme: { bgColors, colors } } = useTheme()
    const categories = useSelector((state) => state.adminData.sett_categories || {})
    const termCategories = useSelector(state => state.userData.termCategories)
    const termCatOptions = !isObjectEmpty(termCategories) ? Object.keys(termCategories).map(val => {
        return { id: val, name: termCategories[val].name[i18nLanguage] }
    }) : []
    const [showFields, setShowFields] = useState([])
    const [fieldsForDropdown, setFieldsForDropdown] = useState([])
    const [selectedField, setSelectedField] = useState("")
    const [showMore, setShowMore] = useState(false)
    const [collapse, setCollapse] = useState(false)
    const allSettings = !isObjectEmpty(formik.values?.settings) ? Object.keys(formik.values?.settings) : []
    const otherSectionSettings = categories && categories[section]?.map(val => val.key)
    const shouldRendered = allSettings?.filter(val => otherSectionSettings?.includes(val))
    const fieldsName = section.toUpperCase() + "_FIELDS"
    const themeState = useSelector(state => state.userData.theme || localStorage.getItem('theme') || config.THEMES_MODES.LIGHT)
    const isContrast = themeState !== config.THEMES_MODES.LIGHT
    const classes = useAutoCompleteStyles(bgColors, colors)()

    const fields = generateFields(categories[section], formik, t, termCatOptions, section)
    let availableFields = config.allTextTypeFields[fieldsName]
        ? config.allTextTypeFields[fieldsName].map(field => ({ ...field, fields: fields[field.key] }))
        : []

    useEffect(() => {
        setShowFields(availableFields
            .filter((field) => shouldRendered.includes(field.name))
        )
        setFieldsForDropdown(availableFields
            .filter((field) => !shouldRendered.includes(field.name))
        )
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [JSON.stringify(shouldRendered), JSON.stringify(termCategories) ])

    const handleAllFields = (event, field, action) => {
        event.preventDefault()
        if (action === "add") {
            setShowFields((prev) => [...prev, field])
            setFieldsForDropdown((prev) => prev.filter((val) => val.id !== field.id))
            formik.setFormikState((prevState) => {
                const newValues = { ...prevState.values }

                field.fields.forEach((f) => {
                    if (!(f.name in newValues)) {
                        let defaultValue
                        switch (f.control) {
                            case "text":
                                defaultValue = ""
                                break
                            case "select":
                            case "number":
                                defaultValue = 0
                                break
                            case "switch":
                                defaultValue = false
                                break
                            default:
                                defaultValue = null
                        }
                        newValues[f.name] = defaultValue
                    }
                })

                return { ...prevState, values: newValues }
            })
        } else if (action === "remove") {
            setFieldsForDropdown((prev) => [...prev, field])
            setShowFields((prev) => prev.filter((val) => val.id !== field.id))
            formik.setFormikState((prevState) => {
                const newValues = { ...prevState.values }
                field.fields.forEach((f) => {
                    delete newValues[f.name]
                })
                return { ...prevState, values: newValues }
            })
        }
    }
    

    const handleSeeButton = (e) => {
        e.preventDefault()
        setShowMore(!showMore)
    }

    return (
        <div>
            <div style={{ backgroundColor: bgColors.bgPrimary0, marginTop: collapse ? 20 : 5 }}>
                <CustomFormRowTitle text={t(section)} collapse={collapse} setCollapse={setCollapse} showMore={showMore} handleSeeButton={handleSeeButton} />
                <Collapse in={collapse} className="mt-1">
                    <div className="mb-3">
                        <div className="table-responsive">
                            <table className="table table-bordered table-striped" style={{ width: "100%" }}>
                                <thead className="table-dark">
                                    <tr>
                                        {config.textTypeTableHeaders.map((header, index) => (
                                            <th key={index} className="text-center" style={{ fontSize: 12 }}>
                                                {t(header)}
                                            </th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {showFields.map((fieldSection) => (
                                        <tr key={fieldSection.id}>
                                            <td className="font-weight-bold mr-4 ml-1 text-center" style={{ fontSize: 12, width: "15%", color: colors.colorText }}>{t(fieldSection.label)}</td>
                                            <td style={{ width: "5%" }}>
                                                {
                                                    fieldSection.fields.filter((field) => field.label === "enabled").map((field) => renderField(field, formik, t, false, colors))
                                                }
                                            </td>
                                            <td className="text-center" >
                                                <div className="d-flex flex-wrap">
                                                    {getOrderedFieldsToRender(fieldSection.fields, showMore, config.fieldSequence, (field) => renderField(field, formik, t, false, colors))}
                                                </div>
                                            </td>
                                            <td className="text-center" style={{ width: "10%" }}>
                                              <Button
                                                    variant="contained"
                                                    color="primary"
                                                    size="small"
                                                    className="mr-2"
                                                    style={{
                                                        padding: "4px",
                                                        minWidth: "30px",
                                                        backgroundColor: bgColors.bgDeleteBtn,
                                                        color: colors.colorPrimary0
                                                    }}
                                                    onClick={(e) => handleAllFields(e, fieldSection, "remove")}
                                                    >
                                                    <Delete fontSize="small" />
                                                </Button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                    {availableFields.length > 0 && <div className="d-flex align-items-center mb-3 ml-1">
                        <FormControl style={{ width: "20%" }}>
                            <Autocomplete
                                options={fieldsForDropdown}
                                getOptionLabel={(option) => t(option.label)}
                                value={fieldsForDropdown.find(field => field.name === selectedField) || null}
                                onChange={(e, newValue) => {
                                    setSelectedField(newValue?.name || "")
                                }}
                                style={{ backgroundColor: bgColors.bgPrimary200, borderRadius: '4px' }}
                                classes={{
                                    paper: isContrast ? classes.paper : '',
                                    option: isContrast ? classes.option : '',
                                    clearIndicator: isContrast ? classes.clearIndicator : '',
                                    noOptions: isContrast ? classes.noOptions : '',
                                    popupIndicator: isContrast ? classes.popupIndicator : '',
                                    inputRoot: isContrast ? classes.inputRoot : ''
                                }}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        InputProps={{ ...params.InputProps, style: { color: colors.colorText } }}
                                        InputLabelProps={{ style: { color: colors.colorText } }}
                                        variant="outlined"
                                        size="small"
                                        label={t('Add a field')}
                                    />
                                )}
                            />
                        </FormControl>
                        {fieldsForDropdown?.length > 0 && <Button variant={"contained"} color={"primary"} style={{ backgroundColor: bgColors.bgBlue1000, color: colors.colorPrimary0 }} className="ml-2" onClick={(e) => handleAllFields(e, fieldsForDropdown.find(val => val.name === selectedField), "add")}><Add /> </Button>
                        }
                    </div>}
                </Collapse>
            </div>
        </div>

    )
}
